import React from 'react';
import type { FearGreedData, FearGreedPoint, FearGreedZone } from '../useFearGreed';
import {
    GAUGE_CX as CX,
    GAUGE_CY as CY,
    GAUGE_NEEDLE_LENGTH as NEEDLE_LENGTH,
    type GaugeZone,
    clampScore,
    gaugeBandPath,
    gaugeNeedleRotation,
} from './gaugeGeometry';

// Bornes IDENTIQUES à api/app/fear_greed/calcul.py::zone_de
// (<25 peur extrême, <45 peur, <=55 neutre, <=75 avidité, sinon avidité extrême).
export const ZONE_INFO: Record<FearGreedZone, { label: string; color: string }> = {
    PEUR_EXTREME: { label: 'Peur extrême', color: '#ef5350' },
    PEUR: { label: 'Peur', color: '#ff9800' },
    NEUTRE: { label: 'Neutre', color: '#fdd835' },
    AVIDITE: { label: 'Avidité', color: '#7cb342' },
    AVIDITE_EXTREME: { label: 'Avidité extrême', color: '#00b638' },
};

export const zoneFearGreed = (score: number): FearGreedZone =>
    score < 25 ? 'PEUR_EXTREME'
        : score < 45 ? 'PEUR'
            : score <= 55 ? 'NEUTRE'
                : score <= 75 ? 'AVIDITE'
                    : 'AVIDITE_EXTREME';

const ZONES: GaugeZone[] = [
    { from: 0, to: 25, color: ZONE_INFO.PEUR_EXTREME.color },
    { from: 25, to: 45, color: ZONE_INFO.PEUR.color },
    { from: 45, to: 55, color: ZONE_INFO.NEUTRE.color },
    { from: 55, to: 75, color: ZONE_INFO.AVIDITE.color },
    { from: 75, to: 100, color: ZONE_INFO.AVIDITE_EXTREME.color },
];

interface FearGreedGaugeProps {
    data: FearGreedData | null;
    theme: Record<string, string>;
}

const Reference: React.FC<{ libelle: string; point: FearGreedPoint | null; theme: Record<string, string> }> = ({
    libelle,
    point,
    theme,
}) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, padding: '3px 0' }}>
        <span style={{ color: theme.sub }}>{libelle}</span>
        {point ? (
            <span style={{ fontWeight: 700, color: ZONE_INFO[point.zone]?.color ?? theme.text }}>
                {Math.round(point.score)} · {ZONE_INFO[point.zone]?.label ?? point.zone}
            </span>
        ) : (
            <span style={{ color: theme.sub }}>—</span>
        )}
    </div>
);

export const FearGreedGauge: React.FC<FearGreedGaugeProps> = ({ data, theme }) => {
    const available = data != null && Number.isFinite(data.score);
    const clamped = available ? clampScore(data.score) : 50;
    const zone = available ? ZONE_INFO[zoneFearGreed(clamped)] : null;

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <svg
                viewBox='0 0 200 108'
                style={{ width: '100%', maxWidth: 240 }}
                role='img'
                aria-label={zone ? `Indice Peur et Avidité : ${Math.round(clamped)}, ${zone.label.toLowerCase()}` : 'Indice Peur et Avidité indisponible'}
            >
                {ZONES.map(z => (
                    <path key={z.from} d={gaugeBandPath(z.from, z.to)} fill={z.color} opacity={zone ? 1 : 0.25} />
                ))}
                {zone && (
                    <g
                        data-testid='fear-greed-needle'
                        style={{
                            transform: `rotate(${gaugeNeedleRotation(clamped)}deg)`,
                            transformOrigin: `${CX}px ${CY}px`,
                            transition: 'transform 1s ease-out',
                        }}
                    >
                        <line x1={CX} y1={CY} x2={CX} y2={CY - NEEDLE_LENGTH} stroke={theme.text} strokeWidth={2.5} strokeLinecap='round' />
                    </g>
                )}
                <circle cx={CX} cy={CY} r={5} fill={theme.text} />
            </svg>

            <div style={{ marginTop: 4, textAlign: 'center' }}>
                <div style={{ fontSize: '12px', color: theme.sub, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Peur &amp; Avidité
                </div>
                {zone && data ? (
                    <>
                        <div style={{ fontSize: 30, fontWeight: 800, color: theme.text, lineHeight: 1.1 }}>{Math.round(clamped)}</div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: zone.color }}>{zone.label}</div>
                    </>
                ) : (
                    <div style={{ marginTop: 4, fontSize: 16, fontWeight: 800, color: theme.sub }}>Données indisponibles</div>
                )}
            </div>

            {data && (
                <div style={{ width: '100%', maxWidth: 260, marginTop: 12 }} data-testid='fear-greed-references'>
                    <Reference libelle='Veille' point={data.references.veille} theme={theme} />
                    <Reference libelle='Semaine dernière' point={data.references.semaine} theme={theme} />
                    <Reference libelle='Mois dernier' point={data.references.mois} theme={theme} />
                    <Reference libelle='Plus haut 1 an' point={data.references.haut_1an} theme={theme} />
                    <Reference libelle='Plus bas 1 an' point={data.references.bas_1an} theme={theme} />
                </div>
            )}
        </div>
    );
};
